import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { of, Observable, throwError, Subscription } from 'rxjs';
import {
  ENVIRONMENT_CONFIG,
  EnvironmentConfig,
  WebsocketGatewayService,
} from '@ipreo/northstar';
import { mergeMap } from 'rxjs/operators';
import { User } from '../types/users.types';
import { PermissionsFacade } from '../facades/permissions.facade';
import { AssignUsersSuccess } from '../state/assignments/actions';
import { AssignmentsUpdateMessage } from '../types/live-update.types';

export interface AssignmentsResponse {
  assignedUsers: User[];
}

@Injectable()
export class AssignmentsService {
  private apiHost;

  private liveSubscription: Subscription;

  getAssignments(dealId): Observable<string[]> {
    const url = `${this.apiHost}/v1/deals/fixed-income/municipal/${dealId}/assignedUsers`;
    return this.http
      .get<AssignmentsResponse>(url, { observe: 'response' })
      .pipe(
        mergeMap((res: HttpResponse<AssignmentsResponse>) => {
          const data = res.status === 200 && res.body;
          return data
            ? of(res.body.assignedUsers.map(({ userId }) => userId))
            : throwError('Error getting assignments');
        })
      );
  }

  assignUsers(dealId, userIds: string[]): Observable<unknown | Error> {
    const url = `${this.apiHost}/v1/deals/fixed-income/municipal/${dealId}/assignedUsers`;

    const body = {
      assignedUsers: userIds,
    };

    return this.http
      .post(url, body, {
        observe: 'response',
      })
      .pipe(
        mergeMap((response: HttpResponse<any[]>) => {
          return response.status === 200
            ? of(userIds)
            : throwError('Cannot assign users');
        })
      );
  }

  constructor(
    private http: HttpClient,
    protected store: Store<unknown>,
    protected permissionsFacade: PermissionsFacade,
    protected websocketGateway: WebsocketGatewayService,
    @Inject(ENVIRONMENT_CONFIG) private config: EnvironmentConfig
  ) {
    this.apiHost = config.params['apiHost'];
  }

  public subscribeToLiveUpdates() {
    this.permissionsFacade.withPermission('assignments').subscribe(() => {
      this.liveSubscription = this.websocketGateway
        .subscribe(this.config.params.assignmentsUpdateChannel as string)
        .subscribe((msg: AssignmentsUpdateMessage) => {
          this.store.dispatch(new AssignUsersSuccess(msg));
        });
    });
  }

  public unsubscribeToLiveUpdates() {
    if (this.liveSubscription) {
      this.liveSubscription.unsubscribe();
    }
  }
}
